/* =============================================================================
   VIEWS / ADMIN / DASHBOARD — Orchestrator (Hauptübersicht)
   =============================================================================
   - renderDashboard: KPIs, Cockpit, offene Anfragen, Heute, Morgen
   - renderCapacityCockpit: 7-Tages-Forecast-Karten mit Klick auf Tagesblatt
   - (S106: manueller Erinnerungs-Pfad entfernt — Reminder laufen automatisch
     über den Cron enqueue_reservation_reminders)

   S202 (REF-8) — dashboard.js in vier Module + diesen Orchestrator zerlegt
   (reine Extraktion, NULL Verhaltensänderung):
     · dashboard-data.js     — pure Statistik/Selektoren (getState, getOrderStats,
                               getOrderTrend, getCockpitCounters, Trend/Heatmap …)
     · dashboard-widgets.js  — Widget-/Heatmap-Renderer + Count-Up-Animation
     · dashboard-timeline.js — „Als Nächstes"-Pane (Timeline+Anfragen, _nextPaneView,
                               _timelineTick)
     · dashboard-lists.js    — Expand-Listen + Anfragen-Zeilen
   Dieser Orchestrator hält Hello-Header, Quick-Actions, Cockpit-Counter-Cluster,
   renderDashboard, renderCapacityCockpit, Live-Registry, Render-Sturm-Check +
   ALLE Re-Exports und die window-Bindings (Union identisch zum Vor-Split-Stand).
   Der Rerender-Rück-Ruf der Sub-Module läuft über setDashboardRerender (data) —
   kein Sub-Modul importiert diesen Orchestrator (acyclisch).

   Externe Helpers, die noch in app.js leben:
   renderReservationItem (Listen-Item) — über window.* aufgelöst (in dashboard-lists).
============================================================================ */

import { htmlEscape, todayISO, tomorrowISO, dateToISO, timeToMinutes } from '../../utils/helpers.js';
import {
  totalCapacity,
  spontaneousReserveSeats,
  getCurrentSlotDuration,
  getDayCapacityUnits,
  computeDayUtilization,
  isActiveReservation,
  getCapacityForecast
} from '../../core/capacity.js';
// S125 — Operator-Layer: die Operator-Kachel ersetzt die Warteliste-Kachel.
import { renderOperatorCard } from './operator-ui.js';
// S237 T2 — „Meine Schicht" ersetzt die Umsatz-Kachel (kein Umsatz aufs Dashboard).
import {
  renderShiftWidget,
  cachedShiftCounters,
  refreshShiftCounters,
  wireShiftEvents
} from './dashboard-shift.js';
import { effectiveRoles, isDashboardLockedForRoles } from '../../core/operator-roles.js';
import { hasCachedOrPendingItems, batchFetchItemsFor } from '../../core/order-items-cache.js';
// Phase 23 (Session 22 Nachschlag): Live-View-Registry — siehe orders.js
import { registerLiveView } from '../../core/live-view-registry.js';

// — S202: aus den Sub-Modulen bezogene Bausteine ——————————————————————————————
import {
  getState,
  // S237 T2: `centsToEUR` wird hier nicht mehr gebraucht — die einzige
  // Euro-Kachel („Umsatz heute") ist entfallen. Die Heatmap-Fußzeile
  // formatiert ihren Wert in dashboard-widgets.js selbst.
  getOrderStats,
  getOrderHeatmap,
  getReservationTrend,
  getOrderTrend,
  computeTrend,
  getCockpitCounters,
  setDashboardRerender
} from './dashboard-data.js';
import {
  renderWidget,
  renderTripleWidget,
  renderOrderHeatmap,
  triggerWidgetAnimations
} from './dashboard-widgets.js';
import { renderNextPane } from './dashboard-timeline.js';

// — S202: Re-Exports, damit die Export-Union von dashboard.js identisch bleibt
// (main.js `import * as adminDashboard` + dashboard.test.js importieren direkt
// aus dieser Datei; die 17 named Exports müssen erhalten bleiben). ——————————————
export {
  getOrderStats,
  getOrderTrend,
  getCockpitCounters,
  renderTripleWidget,
  renderOrderHeatmap
};
export {
  buildTodayTimeline,
  isTimelineOrderDone,   // S240-HF9 — Vorgang abgearbeitet (bezahlt + Tisch frei)
  timelineStage,
  countTodayDone,
  _renderTimelineRow,
  renderNextPane,
  dashboardSetNextView
} from './dashboard-timeline.js';
export { dashboardExpandList, dashboardCollapseList } from './dashboard-lists.js';

/** Springe zum Bestellungs-Tab. Wird im KPI-Klick verwendet. */
function jumpToOrdersTab() {
  if (typeof window.switchTab === 'function') {
    window.switchTab('orders');
    return;
  }
  const btn = document.querySelector('button[data-tab="orders"]');
  if (btn && typeof btn.click === 'function') btn.click();
}

// -----------------------------------------------------------------------------
// v0.30 / Phase 16.2 — Cockpit-Modernisierung (Hello-Header, Quick-Actions).
// -----------------------------------------------------------------------------

/** Begrüßung je nach Tageszeit. */
function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Späte Schicht';
  if (h < 11) return 'Guten Morgen';
  if (h < 14) return 'Mittag-Service';
  if (h < 17) return 'Guten Nachmittag';
  if (h < 22) return 'Abend-Service';
  return 'Schichtende';
}

/** Hübsches deutsches Datum (Heute, Dienstag · 20. Mai 2026). */
function fmtTodayLong() {
  const d = new Date();
  const weekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const months   = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  return `${weekdays[d.getDay()]} · ${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/** Eigene SVG-Icons für die Quick-Actions (Lucide-Look). */
const QA_ICONS = {
  walkin:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><circle cx="9" cy="7" r="3"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>',
  reservation: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M12 14v4M10 16h4"/></svg>',
  orderTake: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/><line x1="9" y1="9" x2="11" y2="9"/></svg>',
  kds:       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><rect x="3" y="4" width="18" height="14" rx="2"/><line x1="8" y1="22" x2="16" y2="22"/><line x1="12" y1="18" x2="12" y2="22"/></svg>',
  bar:       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><path d="M8 22h8"/><path d="M12 11v11"/><path d="M5 3l7 8 7-8z"/></svg>',
  delivery:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><circle cx="6" cy="17" r="2"/><circle cx="17" cy="17" r="2"/><path d="M3 17h1V8a1 1 0 0 1 1-1h7v10"/><path d="M12 11h4l3 3v3h-1"/><path d="M8 17h7"/></svg>',
  guests:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  cart:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/></svg>',
  clock:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>',
  inbox:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>'
};

/**
 * Öffnet das Walk-In-Modal mit "geplante Reservierung"-Default — morgen Abend
 * 19:00 statt 'jetzt'. Wird vom Quick-Action-Button "Reservierung erfassen"
 * aufgerufen. Das Walk-In-Form akzeptiert beliebige Date/Time-Werte; nur die
 * Modal-Überschrift kontextualisiert "Reservierung" statt "Walk-In".
 */
function openAdminReservation() {
  if (typeof window.openWalkInForm !== 'function') return;
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const dateIso = dateToISO(d); // S61 FIX.4: TZ-konsistent statt UTC-Slice
  window.openWalkInForm({ date: dateIso, time: '19:00', adminReservation: true });
}

/** Helper: render Live-Counter-Pill (animiert wenn > 0). */
function _renderCockpitCount(n) {
  if (!n || n <= 0) return '<span class="cockpit-cell-count is-zero">0 offen</span>';
  const label = n === 1 ? '1 offen' : `${n} offen`;
  return `<span class="cockpit-cell-count is-active">${label}</span>`;
}

/** S132b — die 3 Cockpit-Zähler in place aktualisieren (kein Full-Rerender →
 *  kein Freeze-Risiko). Single-Source getCockpitCounters. */
function _updateCockpitCounts() {
  const c = getCockpitCounters();
  const map = { kds: 'cockpit-cell-kds', bar: 'cockpit-cell-bar', delivery: 'cockpit-cell-delivery' };
  for (const key of Object.keys(map)) {
    const cell = document.querySelector('.' + map[key]);
    if (!cell) continue;
    const span = cell.querySelector('.cockpit-cell-count');
    if (span) span.outerHTML = _renderCockpitCount(c[key]);
  }
}

/** S132b — order_items der heute aktiven Orders einmalig nachladen, damit die
 *  item-kanal-genauen Zähler stimmen, falls der Snapshot leer war (admin-Orders).
 *  Loop-sicher: batchFetchItemsFor lädt nur fehlende/nicht-inflight IDs; nach dem
 *  Laden sind sie gecacht → erneuter Aufruf ist ein No-Op (Memory gg-microtask-
 *  render-loop: inflight-aware via hasCachedOrPendingItems). Kein Full-Rerender. */
export async function ensureCockpitItemsLoaded() {
  const state = getState();
  const today = todayISO();
  const ids = (Array.isArray(state.orders) ? state.orders : [])
    .filter(o => o && o.pickupDate === today
      && o.status !== 'cancelled' && o.status !== 'picked_up' && o.status !== 'no_show'
      && !hasCachedOrPendingItems(o.id))
    .map(o => o.id);
  if (!ids.length) return;
  try {
    await batchFetchItemsFor(ids);
    _updateCockpitCounts();
  } catch (e) { console.warn('[dashboard] ensureCockpitItemsLoaded:', e && e.message); }
}

/** Quick-Action-Leiste: 4 essentielle Funktionen + Cockpit-Triade-Kachel.
 *
 *  S62 Jonas-Direktive: Die alten zwei Einzel-Buttons für KDS und Theke
 *  werden zu EINER breiten Cockpit-Triade-Kachel zusammengefasst, die
 *  alle 3 operativen Displays (Küche · Theke · Lieferung) nebeneinander
 *  zeigt — mit Live-Counter pro Bereich. Wirkt interaktiv und erweitert
 *  die Dashboard-Übersicht, ohne sie zu zersplittern.
 *
 *  Reihenfolge: 1. Bestellung aufnehmen · 2. Lieferung erfassen · 3. Reservierung
 *  + breite Cockpit-Triade darunter.
 *
 *  „Lieferung erfassen" + Cockpit-Lieferung-Dritteil sind conditional auf
 *  Settings.delivery — bei deaktivierter Lieferung verschwindet das Triade-Drittel
 *  und der Quick-Action-Button, damit das Layout sauber bleibt.
 */
export function renderQuickActions() {
  const settings = (window.App && window.App.storage && window.App.storage._getInternalState && window.App.storage._getInternalState().settings) || {};
  const deliveryCfg = (settings && settings.delivery) || {};
  const deliveryOn = !!(deliveryCfg.internal_drivers_enabled || deliveryCfg.uber_marketplace_enabled);
  const c = getCockpitCounters();
  return `
    <div class="quick-actions">
      <button class="qa-btn qa-warn" onclick="openAdminOrderEntry()" title="Walk-In, Takeaway oder erst Platz nehmen">
        <span class="qa-icon">${QA_ICONS.orderTake}</span>
        <span class="qa-text">
          <span class="qa-title">Bestellung aufnehmen</span>
          <span class="qa-sub">Walk-In & Takeaway</span>
        </span>
      </button>
      ${deliveryOn ? `
        <button class="qa-btn qa-info" onclick="openDeliveryOrderEntry()" title="Telefonische Liefer-Bestellung erfassen">
          <span class="qa-icon">${QA_ICONS.walkin}</span>
          <span class="qa-text">
            <span class="qa-title">Lieferung erfassen</span>
          </span>
        </button>` : ''}
      <button class="qa-btn qa-accent" onclick="openAdminReservation()" title="Reservierung für die Zukunft erfassen">
        <span class="qa-icon">${QA_ICONS.reservation}</span>
        <span class="qa-text">
          <span class="qa-title">Reservierung erfassen</span>
        </span>
      </button>
    </div>

    <!-- S62 — Cockpit-Triade-Kachel: KDS · Theke · Lieferung in einer breiten Kachel -->
    <div class="cockpit-triad ${deliveryOn ? 'has-delivery' : 'no-delivery'}" role="group" aria-label="Operativ-Cockpits">
      <button class="cockpit-cell cockpit-cell-kds" onclick="switchView('kds')" title="Küchen-Display öffnen (Cmd/Ctrl+Shift+K)">
        <span class="cockpit-cell-icon">${QA_ICONS.kds}</span>
        <span class="cockpit-cell-body">
          <span class="cockpit-cell-title">Küche</span>
          <span class="cockpit-cell-sub">Speisen-Display</span>
        </span>
        ${_renderCockpitCount(c.kds)}
      </button>
      <button class="cockpit-cell cockpit-cell-bar" onclick="switchView('bar')" title="Theken-Display öffnen (Cmd/Ctrl+Shift+B)">
        <span class="cockpit-cell-icon">${QA_ICONS.bar}</span>
        <span class="cockpit-cell-body">
          <span class="cockpit-cell-title">Theke</span>
          <span class="cockpit-cell-sub">Getränke-Display</span>
        </span>
        ${_renderCockpitCount(c.bar)}
      </button>
      ${deliveryOn ? `
        <button class="cockpit-cell cockpit-cell-delivery" onclick="switchView('delivery')" title="Liefer-Cockpit öffnen (Cmd/Ctrl+Shift+L)">
          <span class="cockpit-cell-icon">${QA_ICONS.delivery}</span>
          <span class="cockpit-cell-body">
            <span class="cockpit-cell-title">Lieferung</span>
            <span class="cockpit-cell-sub">Fahrer-Display</span>
          </span>
          ${_renderCockpitCount(c.delivery)}
        </button>` : ''}
    </div>
  `;
}

/** Hello-Header mit Begrüßung + Datum. */
function renderHelloHeader() {
  const authEmail = (window.App && window.App.auth && window.App.auth.getUserEmail && window.App.auth.getUserEmail()) || '';
  const firstName = authEmail ? authEmail.split('@')[0].split(/[.\-_]/)[0] : '';
  const niceName  = firstName ? (firstName.charAt(0).toUpperCase() + firstName.slice(1)) : '';
  return `
    <div class="cockpit-hello">
      <div class="cockpit-hello-title">
        <span class="greeting">${getGreeting()}${niceName ? ', ' + htmlEscape(niceName) : ''}</span>
        <span class="subtitle">${fmtTodayLong()}</span>
      </div>
    </div>
  `;
}

// S125 — renderWaitlistCard entfernt (Operator-Kachel ersetzt die Warteliste).
// Der waitlist-Status bleibt in DB + capacity.js/isReservationDone erhalten —
// separater Cleanup-Cut, nicht mit dem Operator-Layer vermischt.

// S82-6: renderListCard (reservierungs-only „Heute"-Liste) entfernt — durch
// renderTodayTimeline (vollständige Tagesübersicht) ersetzt (jetzt dashboard-timeline).

let _dashboardLiveRegistered = false;
function ensureDashboardLiveRegistered() {
  if (_dashboardLiveRegistered) return;
  _dashboardLiveRegistered = true;
  registerLiveView({
    tabId: 'tab-dashboard',
    render: renderDashboard,
    events: [
      'reservation:realtime',
      'reservation:created',
      'reservation:confirmed',
      'reservation:declined',
      'reservation:noshow',
      'reservation:changed',
      'order:realtime',
      'order:status_changed',
      'order:created',
      'order:cancelled',
      'order:paid'
    ]
  });
}

// S132d — Render-Sturm-Breadcrumb (S105-Instrumentierung, normal still). Schlägt
// an, wenn renderDashboard ungewöhnlich oft in kurzer Zeit feuert (möglicher
// Re-Render-Sturm wie S105). Kein Fix — nur eine Konsolen-Spur, damit Cowork einen
// erneuten Hänger einordnen kann. Microtask-Starvation (Reload hilft NICHT) ist für
// die S131/132-Pfade ausgeschlossen (alle Lazy-Loads inflight-aware, Presence-/
// Cockpit-Updates patchen nur Sub-DOM, operator:changed ist JSON-equality-guarded).
const _dashRenderTimes = [];
function _dashRenderStormCheck() {
  const now = Date.now();
  _dashRenderTimes.push(now);
  while (_dashRenderTimes.length && now - _dashRenderTimes[0] > 2000) _dashRenderTimes.shift();
  if (_dashRenderTimes.length === 12) {
    console.warn('[dashboard][S132d] Render-Sturm-Verdacht: 12 renderDashboard-Aufrufe in <2 s — bitte Spur (Operator-Wechsel? Realtime-Burst?) festhalten.');
  }
}

export function renderDashboard() {
  ensureDashboardLiveRegistered();
  _dashRenderStormCheck();
  const state = getState();
  const today = todayISO();
  const tomorrow = tomorrowISO();
  const all = state.reservations || [];
  const todayRes = all.filter(r => r.date === today && isActiveReservation(r));
  const tomorrowRes = all.filter(r => r.date === tomorrow && isActiveReservation(r));
  // S125 — `waitlist` (Kachel + Card) entfernt; Operator-Kachel ersetzt sie.
  // Der waitlist-Status bleibt in DB/capacity.js (separater Cleanup-Cut).
  const pending = all.filter(r => r.status === 'pending');
  // S192 — offene Bestellungen (status='pending') werden nicht mehr als separate
  // Karte gerendert, sondern im „Anfragen"-Reiter (renderNextPane) je einzeln.
  const totalCap = totalCapacity();
  const todayGuests = todayRes.reduce((s, r) => s + r.guests, 0);
  const utilization = Math.round(computeDayUtilization(today) * 100);
  const orderStats  = getOrderStats(today);
  const orderHeatmap = getOrderHeatmap(4);
  const heatmapMax   = Math.max(1, ...orderHeatmap.map(s => s.count));

  // — Trend-Daten (letzte 7 Tage) für Sparklines + Trend-Pills —
  const resTrend   = getReservationTrend(7);
  const orderTrend = getOrderTrend(7);
  const resTodayCount = resTrend[resTrend.length - 1].count;
  const resYestCount  = resTrend[resTrend.length - 2] ? resTrend[resTrend.length - 2].count : 0;
  // S237 T2: `revenueGross` wird hier NICHT mehr ausgelesen — die Umsatz-Kachel
  // ist der „Meine Schicht"-Kachel gewichen (Jonas: kein Umsatz aufs Dashboard).
  // S122 — Trend-Pills pro Kanal: Bestell. aus `orderCount` (Walk-In+Takeaway),
  // Lieferung aus `deliveryCount`. (Vorher zeigte die Bestell.-Pill `.count` =
  // gesamt; das überzählte Delivery + reservierungs-verknüpfte Orders.)
  const ordTodayCount = orderTrend[orderTrend.length - 1].orderCount;
  const ordYestCount  = orderTrend[orderTrend.length - 2] ? orderTrend[orderTrend.length - 2].orderCount : 0;
  const delTodayCount = orderTrend[orderTrend.length - 1].deliveryCount;
  const delYestCount  = orderTrend[orderTrend.length - 2] ? orderTrend[orderTrend.length - 2].deliveryCount : 0;

  // — 5 Widgets, davon 2 Triple- + 1 Quad-Kachel —
  // 1. Heute (Reservierungen + Bestellungen + Lieferungen)
  // 2. Offen (Anfragen + offene Bestellungen + offene Lieferungen)
  // 3. Operator (S125 — wer ist am Gerät aktiv; Klick → Wechsel)
  // 4. Meine Schicht (S237 T2 — Tätigkeiten des aktiven Operators, kein Umsatz)
  // 5. Max. Gäste/Tag
  const openOrders   = orderStats.order.openCount;     // Walk-In + Takeaway
  const openDelivery = orderStats.delivery.openCount;  // Lieferungen

  // S131b — effektive Operator-Rolle fürs Dashboard-Locking (Single-Source-Matrix).
  // S237 T2: schon HIER geholt, weil die „Meine Schicht"-Kachel den Operator braucht.
  const opApi = (typeof window !== 'undefined') && window.App && window.App.operator;
  const op = (opApi && typeof opApi.getOperator === 'function') ? opApi.getOperator() : null;
  // S167 Multi-Role: Dashboard-Lock = nur wenn ALLE today-fähigen Rollen gesperrt sind.
  const roles = effectiveRoles(op);
  const shiftCounters = cachedShiftCounters(op);

  const widgets = [
    renderTripleWidget({
      label: 'Heute',
      icon: QA_ICONS.clock,
      left:  {
        value: todayRes.length,
        label: 'Reserv.',
        trend: computeTrend(resYestCount, resTodayCount)
      },
      mid: {
        value: orderStats.order.count,
        label: 'Bestell.',
        trend: computeTrend(ordYestCount, ordTodayCount)
      },
      right: {
        value: orderStats.delivery.count,
        label: 'Liefer.',
        trend: computeTrend(delYestCount, delTodayCount)
      },
      // S237 T2: Brutto zeigt das Dashboard bewusst NIRGENDS mehr als Kachel —
      // hier nur Gäste + Auslastung (Umsatz → Auswertung + Tagesabschluss).
      sub: `${todayGuests} Gäste · ${utilization}% Auslastung`
    }),
    renderTripleWidget({
      label: 'Offen',
      icon: QA_ICONS.inbox,
      variant: (pending.length + openOrders + openDelivery) > 0 ? 'accent' : null,
      left:  {
        value: pending.length,
        label: 'Reserv.'
      },
      mid: {
        value: openOrders,
        label: 'Bestell.'
      },
      right: {
        value: openDelivery,
        label: 'Liefer.'
      },
      sub: (() => {
        // reservierungs-verknüpfte Orders laufen über „Als Nächstes"/KDS/Theke,
        // NICHT über diese Pipeline-Summe (bewusste S122-Trennung).
        const inPipeline = (orderStats.order.pipelineCount || 0) + (orderStats.delivery.pipelineCount || 0);
        if (pending.length === 0 && openOrders === 0 && openDelivery === 0 && inPipeline === 0) {
          return 'Alles abgearbeitet 🎉';
        }
        if (pending.length === 0 && openOrders === 0 && openDelivery === 0 && inPipeline > 0) {
          return `${inPipeline} läuft in KDS/Theke`;
        }
        const manual = pending.filter(r => r.requiresManualConfirm).length;
        const parts = [];
        if (manual > 0)       parts.push(`${manual} manuell zu prüfen`);
        if (openOrders > 0)   parts.push(`${openOrders} Bestell. wartet`);
        if (openDelivery > 0) parts.push(`${openDelivery} Lieferung wartet`);
        if (inPipeline > 0)   parts.push(`${inPipeline} in Pipeline`);
        return parts.join(' · ') || 'Warten auf Bearbeitung';
      })()
    }),
    // S125 — Operator-Kachel statt Warteliste: zeigt den aktiven Operator
    // (Initialen + Name), Klick → Wechsel-Modal. (waitlist-Status bleibt in
    // DB/capacity.js — separater Cleanup, hier nur die Kachel ersetzt.)
    renderOperatorCard(),
    // S237 T2 — „Meine Schicht" statt „Umsatz heute": die vier Tätigkeiten des
    // aktiven Operators von heute (Aufgenommen · Zubereitet · Serviert ·
    // Kassiert) aus stats_operator_productivity. Die Zahlen kommen asynchron;
    // gerendert wird mit dem Modul-Cache, den refreshShiftCounters() unten
    // nachzieht und dann NUR in die Kachel patcht (kein Rerender).
    renderShiftWidget({ op, counters: shiftCounters, loading: shiftCounters === null }),
    renderWidget({
      label: 'Max. Gäste/Tag',
      value: Math.round(getDayCapacityUnits()),
      sub: (() => {
        const startMin = timeToMinutes(state.settings.openingTimeStart || '11:00');
        const endMin   = timeToMinutes(state.settings.openingTimeEnd   || '23:00');
        const serviceMin = Math.max(60, endMin - startMin);
        const sittings = Math.max(1, serviceMin / getCurrentSlotDuration());
        const sittingsLabel = (Math.round(sittings * 10) / 10).toString().replace('.', ',');
        return `${totalCap} Plätze × ${sittingsLabel} Sittings · ${spontaneousReserveSeats()} Spontan-Reserve`;
      })(),
      icon: QA_ICONS.guests
    })
  ].join('');

  // (opApi/op/roles stehen oben — die „Meine Schicht"-Kachel braucht den
  //  Operator schon beim Bauen der Widget-Liste.)

  // S132e — „Wer ist aktiv" ist KEINE Dashboard-Section mehr (Initialen-Chips in der
  // Topbar, shell.js). S132e-Layout: „Als Nächstes" wandert nach oben, das
  // Kapazitäts-Cockpit ans Ende (Jonas-Entscheid).
  const html = `
    ${renderHelloHeader()}
    ${renderQuickActions()}
    <div class="widget-grid">${widgets}</div>

    ${renderNextPane(today)}

    ${renderOrderHeatmap(orderHeatmap, heatmapMax, orderStats.total)}

    ${renderCapacityCockpit()}
  `;
  // S60 W3 — „Anstehende Erinnerungen" jetzt im Operativ → Erinnerungen-Tab.
  // „Reservierungen für morgen"-Card komplett aus Dashboard entfernt (Jonas-Direktive).
  const tab = document.getElementById('tab-dashboard');
  if (tab) {
    tab.innerHTML = html;
    // S131b — Staff (oder kein Operator) sieht das VOLLE Dashboard, darf aber nur
    // die Operator-Kachel (+ Topbar-Display-Pills) bedienen. Genau EINE Klasse,
    // Entscheidung aus der Single-Source-Matrix (operator-roles.js) — KEINE
    // verstreuten Rollen-Checks. Beim Operator-Wechsel rendert das Dashboard neu
    // (shell.js operator:changed → switchTab) → Interaktion wird live reaktiviert.
    tab.classList.toggle('dashboard-locked', isDashboardLockedForRoles(roles));
  }

  // S132b — order_items nachladen, damit die Cockpit-Zähler item-kanal-genau werden
  // (admin-Orders haben leeren Snapshot). Fire-and-forget, loop-sicher, kein Rerender.
  try { ensureCockpitItemsLoaded(); } catch (_) { /* tolerant */ }

  // S237 T2 — Schicht-Zähler nachziehen (fire-and-forget, debounced + inflight-
  // guarded; die Antwort patcht NUR `#dash-shift-card`, kein Rerender).
  try { wireShiftEvents(); refreshShiftCounters(); } catch (_) { /* tolerant */ }

  // Count-Up + Flash-Animation für Realtime-Updates triggern
  triggerWidgetAnimations(tab);
}

export function renderCapacityCockpit() {
  const forecast = getCapacityForecast(7);
  const cap = totalCapacity();              // gleichzeitige Plätze (Peak-Basis)
  const dayCap = forecast[0] && forecast[0].dayCapacity || 0;  // Plätze × Sittings (Tages-Basis)
  const criticalDays = forecast.filter(f => f.status === 'critical').length;
  const busyDays     = forecast.filter(f => f.status === 'busy').length;

  const statusLabel = s => ({ free: 'frei', normal: 'normal', busy: 'gut gebucht', critical: 'kritisch' }[s] || s);
  const statusClass = s => 'cockpit-status-' + s;

  const dayCards = forecast.map(f => {
    // Die Bar visualisiert die TAGES-Auslastung (Gäste / Tageskapazität),
    // nicht mehr die einzelne Slot-Spitze — so erkennt man auf einen Blick,
    // wie voll der ganze Tag ist. Die Peak-Info bleibt darunter als Detail.
    const dayBar = Math.min(100, f.dayUtilization);
    return `
      <button class="cockpit-day ${statusClass(f.status)}" onclick="openDailySheet('${f.date}')"
              title="Tagesblatt für ${f.dateLabel} öffnen">
        <div class="cockpit-day-head">
          <span class="cockpit-day-label">${f.dateLabel}</span>
          <span class="cockpit-day-tag">${statusLabel(f.status)}</span>
        </div>
        <div class="cockpit-day-meta">
          <strong>${f.reservations}</strong> Reservierungen ·
          <strong>${f.guests}</strong> Gäste
        </div>
        <div class="cockpit-bar" title="Tages-Auslastung: ${f.guests} von ${f.dayCapacity} möglichen Sittings">
          <div class="cockpit-bar-fill" style="width:${dayBar}%"></div>
        </div>
        <div class="cockpit-day-foot">
          Tag: <strong>${f.guests}/${f.dayCapacity}</strong> (${f.dayUtilization}%)
        </div>
        <div class="cockpit-day-foot cockpit-day-peak">
          ${f.peakSlot
            ? `Peak ${f.peakSlot}: <strong>${f.peakBooked}/${cap}</strong> (${f.peakUtilization}%)`
            : '<span style="color:var(--ink-muted);">Keine Reservierungen</span>'}
        </div>
      </button>
    `;
  }).join('');

  const warningBanner = criticalDays > 0
    ? `<div class="cockpit-warning">⚠ ${criticalDays} Tag${criticalDays>1?'e':''} mit kritischer Auslastung in den nächsten 7 Tagen.</div>`
    : (busyDays >= 3
      ? `<div class="cockpit-warning info">🔔 ${busyDays} Tage mit hoher Auslastung — bald könnte's eng werden.</div>`
      : '');

  return `
    <div class="card" style="margin-top: 20px;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 8px; flex-wrap: wrap; gap: 8px;">
        <h2 style="margin:0;">Kapazitäts-Cockpit · nächste 7 Tage</h2>
        <span style="font-size:.8rem; color:var(--ink-muted);">Klick auf einen Tag → Tagesblatt</span>
      </div>
      <p style="font-size:.78rem; color:var(--ink-muted); margin: 0 0 12px;">
        <strong>Tag</strong>: gesamte über den Tag buchbare Plätze (Sitzplätze × Sittings = ${dayCap}).
        <strong>Peak</strong>: gleichzeitige Spitzenbelegung im vollsten Zeitslot (max. ${cap}).
      </p>
      ${warningBanner}
      <div class="cockpit-grid">${dayCards}</div>
    </div>
  `;
}

// S106 — Manueller Erinnerungs-Pfad entfernt. `getUpcomingForReminder` +
// `renderReminderCenter` (48h-Liste mit „Erinnerung senden"-Buttons) sind raus,
// damit es keinen zweiten, konkurrierenden Reminder-Versand mehr gibt. Die
// Erinnerungen laufen jetzt ausschließlich automatisch über den Cron
// `enqueue_reservation_reminders` (idempotent via `reminder_enqueued_at`) →
// genau 1× pro Reservierung zur in den Settings eingestellten Vorlaufzeit.

// S202 — Rerender-Rück-Ruf registrieren: dashboard-lists (Expand/Collapse) und
// dashboard-timeline (SetNextView-Fallback) lösen über requestDashboardRerender
// (data) ein volles renderDashboard aus, ohne den Orchestrator zu importieren.
setDashboardRerender(renderDashboard);

// Backward-Compat — window-Bindings-Union identisch zum Vor-Split-Stand:
// die Sub-Module binden ihre eigenen Inline-Handler selbst (renderOrderHeatmap →
// widgets; dashboardExpandList/-CollapseList → lists; dashboardSetNextView →
// timeline); der Orchestrator bindet die restlichen vier.
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderDashboard,
    renderCapacityCockpit,
    jumpToOrdersTab,
    openAdminReservation
  });
}
